"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "../../lib/cn";

type Country = { code: string; iso: string; name: string };

const COUNTRIES: Country[] = [
  { code: "DE", iso: "de", name: "Deutschland" },
  { code: "AT", iso: "at", name: "Österreich" },
  { code: "CH", iso: "ch", name: "Schweiz" },
  { code: "FR", iso: "fr", name: "France" },
  { code: "NL", iso: "nl", name: "Nederland" },
  { code: "BE", iso: "be", name: "België" },
  { code: "LU", iso: "lu", name: "Luxembourg" },
  { code: "IT", iso: "it", name: "Italia" },
  { code: "PL", iso: "pl", name: "Polska" },
  { code: "CZ", iso: "cz", name: "Česko" },
  { code: "DK", iso: "dk", name: "Danmark" },
  { code: "ES", iso: "es", name: "España" },
  { code: "GB", iso: "gb", name: "United Kingdom" },
  { code: "PT", iso: "pt", name: "Portugal" },
  { code: "SE", iso: "se", name: "Sverige" },
  { code: "NO", iso: "no", name: "Norge" },
  { code: "FI", iso: "fi", name: "Suomi" },
  { code: "HU", iso: "hu", name: "Magyarország" },
  { code: "RO", iso: "ro", name: "România" },
  { code: "SK", iso: "sk", name: "Slovensko" },
  { code: "SI", iso: "si", name: "Slovenija" },
  { code: "HR", iso: "hr", name: "Hrvatska" },
];

export function CountrySelect({
  id,
  value,
  onChange,
  className,
  buttonClassName,
}: {
  /** Forwarded onto the trigger button so a FloatField <label htmlFor> can
   *  target it (used when the select sits inside a floating-label field). */
  id?: string;
  value: string;
  onChange: (code: string) => void;
  className?: string;
  buttonClassName?: string;
}) {
  const t = useTranslations("Booking");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // When a Google place resolves a country outside our served list (e.g.
  // "US", "IE"), show that actual code/flag instead of silently falling
  // back to Deutschland — otherwise the select displayed "DE" while the
  // stored value (and the summary / booking) said "US". A synthetic entry
  // keeps display and value consistent; flag-icons covers most ISO codes.
  const current =
    COUNTRIES.find((c) => c.code === value) ??
    (value
      ? { code: value, iso: value.toLowerCase(), name: value }
      : COUNTRIES[0]);
  const filtered = query
    ? COUNTRIES.filter(
        (c) =>
          c.code.toLowerCase().includes(query.toLowerCase()) ||
          c.name.toLowerCase().includes(query.toLowerCase()),
      )
    : COUNTRIES;

  function pick(code: string) {
    onChange(code);
    setOpen(false);
    setQuery("");
    setActive(0);
  }

  function onListKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && filtered[active]) {
      e.preventDefault();
      pick(filtered[active].code);
    }
  }

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex h-full min-h-[40px] w-full items-center gap-1.5 rounded-md border border-line-200 bg-white px-2.5 text-sm font-medium text-ink-900 transition-colors hover:border-ink-900/30",
          buttonClassName,
        )}
      >
        <span className={`fi fi-${current.iso} fis !rounded-sm h-3 w-3.5`} aria-hidden />
        <span className="tabnums text-[13px]">{current.code}</span>
        <ChevronDown className="ml-auto h-3 w-3 text-ink-400" aria-hidden />
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-50 mt-1 w-60 overflow-hidden rounded-md border border-line-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-line-200 px-2.5 py-1.5">
            <Search className="h-3 w-3 text-ink-400" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onListKey}
              placeholder={t("countrySearch")}
              className="flex-1 bg-transparent text-base placeholder:text-ink-400 focus:outline-none md:text-[13px]"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-auto py-1">
            {filtered.map((c, i) => (
              <li key={c.code} role="option" aria-selected={c.code === value}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(c.code);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[13px] transition-colors",
                    i === active ? "bg-soft" : "bg-white",
                    c.code === value && "font-semibold",
                  )}
                >
                  <span className={`fi fi-${c.iso} fis !rounded-sm h-3 w-3.5`} aria-hidden />
                  <span className="flex-1 text-ink-900">{c.name}</span>
                  <span className="text-[11px] text-ink-400 tabnums">{c.code}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-[13px] text-ink-500">{t("noMatch")}</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
